import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { NewIssueFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newIssueTriggerModel';
import { PlaybooksTriggerConfigInput } from 'src/app/models/componentInputs/playbooksTriggerInput.interface';
import { ISelectOption } from 'src/app/models/selectOptions';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject } from '@angular/core';
import { IssueSeverity, IssueTaxonomy } from 'src/app/models/api/issues/issue-enums.model';
import { createSelectOptionsFromEnum } from 'src/app/common/selectOptionsUtils';
import { IssueAiServiceSelectOptionsForPlaybooks } from 'src/app/models/selectOptions/issueAiServiceSelectOptions.model';

@Component({
  selector: 'app-new-issue-trigger',
  templateUrl: './new-issue-trigger.component.html',
  styleUrls: ['./new-issue-trigger.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewIssueTriggerComponent implements OnInit {
  @Input() public fg: UntypedFormGroup;
  @Input() public playbooksTriggerInput: PlaybooksTriggerConfigInput;
  @Output() public validConfiguration = new EventEmitter<boolean>();

  public severityOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public taxonomyOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public aiServiceOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  private readonly _destroyRef = inject(DestroyRef);

  constructor(
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _beautifyPipe: BeautifyDisplayStringPipe
  ) {}

  public ngOnInit() {
    this._initializeForm();
    this._loadOptions();
    this._setupFormValidation();
  }

  private _initializeForm() {
    if (!this.fg.contains('severity')) {
      this.fg.addControl('severity', this._formBuilder.control([]));
    }
    if (!this.fg.contains('taxonomy')) {
      this.fg.addControl('taxonomy', this._formBuilder.control([]));
    }
    if (!this.fg.contains('aiServices')) {
      this.fg.addControl('aiServices', this._formBuilder.control([]));
    }

    if (this.playbooksTriggerInput.triggerConfig) {
      const config = this.playbooksTriggerInput.triggerConfig as NewIssueFoundTriggerConfigModel;
      this.fg.patchValue({
        severity: config.severity || [],
        taxonomy: config.taxonomy || [],
        aiServices: config.aiServices || [],
      });
    }
  }

  private _loadOptions() {
    this.severityOptions$.next(createSelectOptionsFromEnum(IssueSeverity, this._beautifyPipe));
    this.taxonomyOptions$.next(createSelectOptionsFromEnum(IssueTaxonomy, this._beautifyPipe));
    this.aiServiceOptions$.next(IssueAiServiceSelectOptionsForPlaybooks);
  }

  private _setupFormValidation() {
    this.fg.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      const isValid = this._isFormValid();
      this.validConfiguration.emit(isValid);
    });
  }

  private _isFormValid(): boolean {
    const severity = this.fg.get('severity')?.value;
    const taxonomy = this.fg.get('taxonomy')?.value;
    const aiServices = this.fg.get('aiServices')?.value;

    return (
      (severity && severity.length > 0) || (taxonomy && taxonomy.length > 0) || (aiServices && aiServices.length > 0)
    );
  }

  public updateMultiSelectControl(controlName: string, options: ISelectOption[]) {
    const selectedValues = options
      .filter((option) => option && option.value !== undefined)
      .map((option) => option.value);
    const control = this.fg.get(controlName);
    if (control) {
      control.setValue(selectedValues);
    }
  }
}
